import { shallowRef } from 'vue';
import store from '~/store';
import { defineStore } from 'pinia';
import { FFmpeg } from '@ffmpeg/ffmpeg';
import { fetchFile, toBlobURL } from '@ffmpeg/util';
import type { LogEvent, ProgressEvent, ProgressEventCallback } from '@ffmpeg/ffmpeg';

export const ffmpegStore = defineStore('ffmpeg', () => {
  const ffmpeg = shallowRef<FFmpeg>();

  const loadFFmpeg = async () => {
    // /ffmpeg
    // https://unpkg.com/@ffmpeg/core@0.12.2/dist/umd
    // https://unpkg.com/@ffmpeg/core@0.12.6/dist/esm
    // https://cdn.jsdelivr.net/npm/@ffmpeg/core@0.12.6/dist/esm/ffmpeg-core.wasm
    // https://unpkg.zhimg.com/@ffmpeg/core@0.12.6/dist/esm/ffmpeg-core.wasm
    // https://s4.zstatic.net/npm/@ffmpeg/core@0.12.6/dist/esm/ffmpeg-core.wasm
    const baseURL = 'https://unpkg.zhihu.com/@ffmpeg/core@0.12.10/dist/esm';
    ffmpeg.value = new FFmpeg();
    ffmpeg.value.on('log', (_e: LogEvent) => {
      // console.log(_e.message, _e.type);
    });
    ffmpeg.value.on('progress', (_e: ProgressEvent) => {
      // console.log(e.progress);
      // if (e.progress > 1) return;
      // progress.value = Math.floor(e.progress * 100);
    });
    await ffmpeg.value.load({
      coreURL: await toBlobURL(`${baseURL}/ffmpeg-core.js`, 'text/javascript'),
      wasmURL: await toBlobURL(`${baseURL}/ffmpeg-core.wasm`, 'application/wasm'),
      //   workerURL: await toBlobURL(`${baseURL}/ffmpeg-core.worker.js`, 'text/javascript'),
    });
  };

  const transcode = async (videoURL: string, progressFunc: (progress: ProgressEvent) => void) => {
    const promise = () =>
      new Promise<Blob>(async (resolve) => {
        if (!ffmpeg.value) await loadFFmpeg();
        console.log(videoURL);
        ffmpeg.value?.on('progress', progressFunc);

        await ffmpeg.value?.writeFile('raw.avi', await fetchFile(videoURL));
        await ffmpeg.value?.exec(['-i', 'raw.avi', 'target.mp4']);
        const data = (await ffmpeg.value?.readFile('target.mp4')) as any;
        resolve(new Blob([data.buffer], { type: 'video/mp4' }));
      });
    const toastPromiseResult = toast.promise(promise, {
      description: '浏览器不包含该视频解码器',
      loading: '正在尝试使用ffmpeg转码...',
      success: () => '视频转码成功!',
      error: () => '视频解析失败!请手动更换视频格式或使用ffmpeg转码',
    });
    const result = toastPromiseResult ? await toastPromiseResult.unwrap() : null;
    return result;
  };

  const toGif = async (
    videoURL: string,
    width: number,
    height: number,
    fps: number,
    speed: number,
    maxColors: number,
    start: number,
    duration: number,
    signal: AbortSignal,
    func: ProgressEventCallback,
  ) => {
    if (!ffmpeg.value) await loadFFmpeg();
    ffmpeg.value?.on('progress', func);

    await ffmpeg.value?.writeFile('raw.mp4', await fetchFile(videoURL));
    // lanczos缩放算法以获得更好的图像质量
    // split[s0][s1]将视频流分割为两个相同的流
    // [s0]palettegen=max_colors=255[p]生成一个最多包含255种颜色的调色板
    // [s1][p]paletteuse则使用这个调色板来创建GIF
    const args = [
      '-i',
      'raw.mp4',
      '-ss',
      `${start}`,
      '-t',
      `${duration}`,
      '-vf',
      `fps=${fps},setpts=${1 / speed}*PTS,scale=${width}:${height}:flags=lanczos,split[s0][s1];[s0]palettegen=max_colors=${Math.max(maxColors, 4)}[p];[s1][p]paletteuse`,
      '-y',
      'target.gif',
    ];
    await ffmpeg.value?.exec(args, -1, { signal });
    const data = (await ffmpeg.value?.readFile('target.gif')) as any;
    return new Blob([data], { type: 'image/gif' });
  };

  return { ffmpeg, loadFFmpeg, transcode, toGif };
});

/** 在 setup 外使用 */
export function useFFmpegStoreHook() {
  return ffmpegStore(store);
}
